import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Link2, Loader2, Pencil, UserX, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { einsatzAPI } from '@/lib/api';

/* Trainer (Einsatzplanung Schritt 1, 16.10.2026, backend/einsatz/trainer.py). Betreiber: Ein Trainer ist ein App-Konto mit
   der Rolle Trainer oder Leitung (`trainer_id` = leads.id); Trainer-Links sind Geräte bzw. Links, keine Trainer. Der
   Abgleich schlägt vor, welche Links, CRM-Nutzer, /coach-Zugänge und Schreibweisen aus dem Kursplan zu wem gehören —
   die Leitung hakt an und übernimmt. Mehrdeutiges ist rot und nicht vorgehakt, Vergebenes lässt sich nicht wählen.
   Schreiben darf nur die Leitung (`darf_schreiben` vom Server); feste Farben gibt es nur als Trainerfarbe. */

const STAND = {
  bestaetigt: { text: 'Abgleich bestätigt', variant: 'default' },
  profil: { text: 'Profil, Abgleich offen', variant: 'secondary' },
  offen: { text: 'noch kein Profil', variant: 'outline' },
};
const ROLLE = { admin: 'Leitung', trainer: 'Trainer' };
const GRUND = { name: 'Name', email: 'E-Mail', konto: 'am App-Konto', von_hand: 'von Hand' };
const GRUPPEN = [
  { feld: 'links', titel: 'Trainer-Links', ziel: 'trainer_link_ids', einzeln: false },
  { feld: 'nutzer', titel: 'CRM-Nutzer', ziel: 'user_id', einzeln: true },
  { feld: 'coaches', titel: '/coach-Zugang', ziel: 'coach_id', einzeln: true },
  { feld: 'namen', titel: 'Schreibweisen im Kursplan', ziel: 'namen', einzeln: false },
];

function fehlertext(e, ersatz) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return 'Bitte die Eingaben prüfen.';
  return ersatz;
}

function Farbpunkt({ farbe }) {
  return <span aria-hidden className="inline-block h-3 w-3 shrink-0 rounded-full border border-border" style={{ backgroundColor: farbe || 'transparent' }} />;
}

function Kuerzel({ profil }) {
  return (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-xs font-semibold"
      style={profil.farbe ? { borderColor: profil.farbe, color: profil.farbe } : undefined}>
      {profil.kuerzel || '?'}
    </span>
  );
}

/* ── Profil + Qualifikation ── */

function ProfilDialog({ eintrag, kursarten, mandanten, darf, onZu, onGespeichert }) {
  return (
    <Dialog open={!!eintrag} onOpenChange={(o) => { if (!o) onZu(); }}>
      <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)] rounded-xl" data-testid="einsatz-profil-dialog">
        {eintrag && <ProfilFormular eintrag={eintrag} kursarten={kursarten} mandanten={mandanten} darf={darf} onZu={onZu} onGespeichert={onGespeichert} />}
      </DialogContent>
    </Dialog>
  );
}

function ProfilFormular({ eintrag, kursarten, mandanten, darf, onZu, onGespeichert }) {
  const p = eintrag.profil;
  const [anzeigename, setAnzeigename] = useState(p.anzeigename || '');
  const [kuerzel, setKuerzel] = useState(p.kuerzel || '');
  const [farbe, setFarbe] = useState(p.farbe || '');
  const [aktiv, setAktiv] = useState(p.aktiv !== false);
  const [mand, setMand] = useState(p.mandanten?.length ? p.mandanten : ['p1']);
  const [quali, setQuali] = useState(p.qualifikation || []);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');

  const umschalten = (liste, setListe, key) => setListe(liste.includes(key) ? liste.filter((x) => x !== key) : [...liste, key]);

  const speichern = async () => {
    setBusy(true); setFehler('');
    try {
      await einsatzAPI.profilSpeichern(eintrag.trainer_id, {
        anzeigename: anzeigename.trim(), kuerzel: kuerzel.trim() || undefined, farbe: farbe.trim() || null, aktiv, mandanten: mand,
      });
      await einsatzAPI.qualifikationSetzen(eintrag.trainer_id, quali);
      toast.success('Profil gespeichert');
      onGespeichert();
    } catch (e) {
      setFehler(fehlertext(e, 'Das Profil wurde nicht gespeichert.'));
    } finally { setBusy(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Kuerzel profil={{ ...p, kuerzel, farbe }} /> {eintrag.name}</DialogTitle>
        <DialogDescription>Kennung: App-Konto ({ROLLE[eintrag.app_role] || 'ohne Rolle'})</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_6rem]">
          <div className="space-y-1">
            <Label htmlFor="ep-name">Anzeigename</Label>
            <Input id="ep-name" value={anzeigename} disabled={!darf} maxLength={60} onChange={(e) => setAnzeigename(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ep-kuerzel">Kürzel</Label>
            <Input id="ep-kuerzel" value={kuerzel} disabled={!darf} maxLength={4} onChange={(e) => setKuerzel(e.target.value.toUpperCase())} />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="ep-farbe">Farbe im Plan</Label>
          <div className="flex items-center gap-2">
            <input id="ep-farbe" type="color" disabled={!darf} className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
              value={farbe || '#888888'} onChange={(e) => setFarbe(e.target.value.toUpperCase())} />
            <Input value={farbe} disabled={!darf} placeholder="#RRGGBB" className="w-32" onChange={(e) => setFarbe(e.target.value)} />
            {farbe && darf && <Button variant="ghost" size="sm" onClick={() => setFarbe('')}>ohne Farbe</Button>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={aktiv} disabled={!darf} onCheckedChange={(v) => setAktiv(!!v)} /> aktiv (wird eingeplant)
          </label>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">Mandanten:</span>
            {mandanten.map((m) => (
              <label key={m} className="flex items-center gap-1.5">
                <Checkbox checked={mand.includes(m)} disabled={!darf} onCheckedChange={() => umschalten(mand, setMand, m)} /> {m.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Qualifikation</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="einsatz-quali">
            {kursarten.map((k) => {
              const an = quali.includes(k.key);
              return (
                <button key={k.key} type="button" disabled={!darf} onClick={() => umschalten(quali, setQuali, k.key)}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${an ? 'border-marke bg-marke/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'} disabled:cursor-default`}
                  aria-pressed={an}>
                  {k.label}
                </button>
              );
            })}
          </div>
        </div>
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onZu} disabled={busy}>{darf ? 'Abbrechen' : 'Schließen'}</Button>
        {darf && <Button onClick={speichern} disabled={busy || anzeigename.trim().length < 2 || !mand.length}>
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Speichern
        </Button>}
      </DialogFooter>
    </>
  );
}

/* ── Abgleich je Trainer ── */

function startAuswahl(v) {
  const a = { trainer_link_ids: [], user_id: null, coach_id: null, namen: [] };
  for (const g of GRUPPEN) {
    for (const x of v[g.feld] || []) {
      if (!x.vorgeschlagen || (x.vergeben_an && !x.verknuepft)) continue;
      if (g.einzeln) { if (!a[g.ziel]) a[g.ziel] = x.id; } else a[g.ziel].push(x.id);
    }
  }
  return a;
}

function AbgleichTrainer({ v, darf, onUebernommen }) {
  const [auswahl, setAuswahl] = useState(() => startAuswahl(v));
  const [busy, setBusy] = useState(false);
  const leer = GRUPPEN.every((g) => !(v[g.feld] || []).length);

  const an = (g, id) => (g.einzeln ? auswahl[g.ziel] === id : auswahl[g.ziel].includes(id));
  const umschalten = (g, id) => setAuswahl((alt) => {
    if (g.einzeln) return { ...alt, [g.ziel]: alt[g.ziel] === id ? null : id };
    const liste = alt[g.ziel];
    return { ...alt, [g.ziel]: liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id] };
  });

  const uebernehmen = async () => {
    setBusy(true);
    try {
      await einsatzAPI.abgleichUebernehmen(v.trainer_id, auswahl);
      toast.success(`Abgleich für ${v.name} übernommen`);
      onUebernommen();
    } catch (e) {
      toast.error(fehlertext(e, 'Der Abgleich wurde nicht übernommen.'));
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-3 ${v.mehrdeutig ? 'border-destructive/50' : 'border-border'}`} data-testid={`abgleich-${v.trainer_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{v.name}</span>
          <Badge variant="outline">{ROLLE[v.app_role] || v.app_role}</Badge>
          <Badge variant={STAND[v.stand]?.variant || 'outline'}>{STAND[v.stand]?.text || v.stand}</Badge>
          {v.mehrdeutig && <span className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> mehrdeutig</span>}
        </div>
        {darf && <Button size="sm" onClick={uebernehmen} disabled={busy}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Übernehmen</Button>}
      </div>
      {leer
        ? <p className="mt-2 text-sm text-muted-foreground">Nichts gefunden, was zu diesem Konto passt. Übernehmen legt das Profil ohne Verknüpfungen an.</p>
        : (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {GRUPPEN.map((g) => {
              const liste = v[g.feld] || [];
              if (!liste.length) return null;
              return (
                <div key={g.feld} className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{g.titel}{g.einzeln ? ' (eins)' : ''}</p>
                  {liste.map((x) => {
                    const gesperrt = !!x.vergeben_an && !x.verknuepft;
                    return (
                      <label key={`${g.feld}-${x.id}`} className={`flex items-start gap-2 rounded-md px-1.5 py-1 text-sm ${x.mehrdeutig ? 'bg-destructive/5 text-destructive' : ''} ${gesperrt ? 'opacity-60' : ''}`}>
                        <Checkbox className="mt-0.5" checked={an(g, x.id)} disabled={!darf || gesperrt} onCheckedChange={() => umschalten(g, x.id)} />
                        <span className="min-w-0">
                          <span className="font-medium">{x.name || x.id}</span>
                          <span className="ml-1 text-xs text-muted-foreground">· {GRUND[x.grund] || x.grund}</span>
                          {x.mehrdeutig && <span className="block text-xs">passt auch zu {x.andere.join(', ')}</span>}
                          {gesperrt && <span className="block text-xs text-muted-foreground">gehört zu {x.vergeben_an}</span>}
                          {x.kurse?.length > 0 && <span className="block text-xs text-muted-foreground">{x.kurse.slice(0, 4).join(' · ')}{x.kurse.length > 4 ? ` · +${x.kurse.length - 4}` : ''}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

/* ── Seite ── */

export default function EinsatzTrainerPage() {
  const [liste, setListe] = useState(null);
  const [abgleich, setAbgleich] = useState(null);
  const [ladeFehler, setLadeFehler] = useState('');
  const [offen, setOffen] = useState(null);
  const [stand, setStand] = useState(0);

  const laden = useCallback(async () => {
    setLadeFehler('');
    try {
      const [l, a] = await Promise.all([einsatzAPI.trainer(), einsatzAPI.abgleich()]);
      setListe(l.data); setAbgleich(a.data); setStand((s) => s + 1);
    } catch (e) {
      setLadeFehler(fehlertext(e, 'Die Trainer konnten nicht geladen werden.'));
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const kursarten = useMemo(() => liste?.kursarten || [], [liste]);
  const label = useMemo(() => Object.fromEntries(kursarten.map((k) => [k.key, k.label])), [kursarten]);
  const darf = !!liste?.darf_schreiben;

  if (!liste || !abgleich) {
    return (
      <div className="max-w-5xl space-y-4" data-testid="einsatz-trainer-seite">
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Users className="h-6 w-6 text-marke" /> Trainer</h1>
        {ladeFehler
          ? <Card><CardContent className="space-y-3 p-5"><p className="text-sm">{ladeFehler}</p><Button variant="outline" size="sm" onClick={laden}>Erneut versuchen</Button></CardContent></Card>
          : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …</div>}
      </div>
    );
  }

  const ohne = abgleich.ohne_app_konto || [];
  const linksOhne = abgleich.links_ohne_app_konto || [];
  const ausgeschlossen = abgleich.ausgeschlossen || [];

  return (
    <div className="max-w-5xl space-y-5" data-testid="einsatz-trainer-seite">
      <div className="min-w-0">
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Users className="h-6 w-6 text-marke" /> Trainer</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Ein Trainer ist ein App-Konto mit der Rolle Trainer oder Leitung. Trainer-Links (Säulen-iPad, Tippgeber) sind Geräte, keine Trainer.
          {!darf && ' Ändern kann nur die Leitung.'}
        </p>
      </div>

      <Card>
        <CardContent className="space-y-1 p-4">
          <h2 className="mb-2 text-base font-semibold">Trainer ({liste.trainer.length})</h2>
          {liste.trainer.length === 0 && <p className="text-sm text-muted-foreground">Noch kein App-Konto mit der Rolle Trainer oder Leitung.</p>}
          <ul className="divide-y divide-border">
            {liste.trainer.map((t) => {
              const p = t.profil;
              return (
                <li key={t.trainer_id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`trainer-${t.trainer_id}`}>
                  <Kuerzel profil={p} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{p.anzeigename || t.name}</span>
                      {p.anzeigename && p.anzeigename !== t.name && <span className="text-xs text-muted-foreground">({t.name})</span>}
                      <Badge variant="outline">{ROLLE[t.app_role] || 'ohne Rolle'}</Badge>
                      <Badge variant={STAND[t.stand]?.variant || 'outline'}>{STAND[t.stand]?.text || t.stand}</Badge>
                      {p.aktiv === false && <Badge variant="secondary">inaktiv</Badge>}
                      {t.ohne_rolle && <span className="flex items-center gap-1 text-xs text-destructive"><UserX className="h-3.5 w-3.5" /> App-Rolle fehlt</span>}
                      {p.farbe && <Farbpunkt farbe={p.farbe} />}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1 text-xs text-muted-foreground">
                      <span>{(p.mandanten || []).map((m) => m.toUpperCase()).join(' · ')}</span>
                      {(p.verknuepft?.trainer_link_ids?.length > 0) && <span className="flex items-center gap-0.5"><Link2 className="h-3 w-3" /> {p.verknuepft.trainer_link_ids.length} Link(s)</span>}
                      {(p.qualifikation || []).map((q) => <span key={q} className="rounded-full border border-border px-1.5">{label[q] || q}</span>)}
                      {!(p.qualifikation || []).length && <span>ohne Qualifikation</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setOffen(t)}><Pencil className="mr-1 h-3.5 w-3.5" /> {darf ? 'Bearbeiten' : 'Ansehen'}</Button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div>
            <h2 className="text-base font-semibold">Abgleich</h2>
            <p className="text-sm text-muted-foreground">
              Vorschläge aus Namen und E-Mail-Adressen — es wird nichts geraten: Erst „Übernehmen“ verknüpft. Rot = passt zu mehreren Konten, bitte von Hand entscheiden.
            </p>
          </div>
          {abgleich.trainer.map((v) => <AbgleichTrainer key={`${v.trainer_id}-${stand}`} v={v} darf={darf} onUebernommen={laden} />)}
        </CardContent>
      </Card>

      {(ohne.length > 0 || linksOhne.length > 0 || ausgeschlossen.length > 0) && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="flex items-center gap-2 text-base font-semibold"><UserX className="h-4 w-4 text-marke" /> Ohne App-Konto</h2>
            <p className="text-sm text-muted-foreground">{abgleich.hinweis}</p>
            {ohne.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Namen im Kursplan</p>
                {ohne.map((o) => (
                  <div key={o.name} className="text-sm"><span className="font-medium">{o.name}</span>
                    <span className="ml-1 text-xs text-muted-foreground">{o.kurse.join(' · ')}</span></div>
                ))}
              </div>
            )}
            {linksOhne.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Trainer-Links ohne passendes Konto</p>
                <p className="text-sm">{linksOhne.map((l) => l.name).join(' · ')}</p>
              </div>
            )}
            {ausgeschlossen.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Nicht im Abgleich</p>
                <p className="text-sm text-muted-foreground">
                  {ausgeschlossen.map((l) => `${l.name} (${l.grund === 'geraet' ? 'Gerät' : 'gesperrt'})`).join(' · ')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {abgleich.trainer.length > 0 && abgleich.trainer.every((v) => v.stand === 'bestaetigt') && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-marke" /> Alle Trainer abgeglichen.</p>
      )}

      <ProfilDialog eintrag={offen} kursarten={kursarten} mandanten={liste.mandanten || ['p1', 'ptl']} darf={darf}
        onZu={() => setOffen(null)} onGespeichert={() => { setOffen(null); laden(); }} />
    </div>
  );
}
